/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, { useContext, useState, useRef, useEffect } from "react";
import { Button } from "baseui/button";
import { ProjectContext } from "../../UserApp";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { IoMdDownload } from "react-icons/io";
import { PathType } from "../../Api/DataTransformation";
import { useTransformEngineStyles } from "../TransformNodes/TransformEngineStyles";
import NodeLogo from "../NodeLogo";
import { CreateTransformEngineNodeHandle } from "../TransformNodes/TransformEngineNodeComponent";
import {
  nodeHasEdge,
  TransformEngineNodeData,
  TransformEngineNodeHandle,
} from "../TransformNodes/Util/TransformEngineDataUtils";
import {
  normalizeRawMappingResult,
  isFileInfoPair,
  toFileTypeEnum,
} from "../TransformNodes/MappingResultUtils";
import { FileType, getFileMimeType, OutputNodeDescriptor } from "Api";
import { loggedNotify, Component } from "../../Shared/Notify";
import { AxiosContext } from "../../Utils/AuthContext";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";

export interface OutputNodeProps {
  data: TransformEngineNodeData;
}

const CIRCLE_SIZE = 100; // px
const HANDLE_SIZE = 30;

function parseMappingResult(mappingResult: any): {
  content: string | null;
  fileType: string | null;
  fileName: string | null;
  isError: boolean;
  errorMessage?: string;
} {
  if (!mappingResult) {
    return {
      content: null,
      fileType: null,
      fileName: null,
      isError: true,
      errorMessage: "No mappingResult",
    };
  }

  if (typeof mappingResult === "object") {
    return {
      content: mappingResult.content ?? null,
      fileType: mappingResult.fileType ?? null,
      fileName: mappingResult.fileName ?? null,
      isError: !(mappingResult.content && mappingResult.fileType),
      errorMessage: !(mappingResult.content && mappingResult.fileType)
        ? "Structured mappingResult missing content or fileType"
        : undefined,
    };
  }

  if (typeof mappingResult === "string") {
    const contentMatch = mappingResult.match(/content=(.*?),\s*fileType=/s);
    const typeMatch = mappingResult.match(/fileType=([A-Za-z0-9_]+)/);
    const nameMatch = mappingResult.match(/fileName=([^\)\n\r]+)/);
    const content = contentMatch ? contentMatch[1].trim() : null;
    const fileName = nameMatch ? nameMatch[1] : null;
    const fileType = typeMatch ? typeMatch[1] : null;
    const isError = !content || !fileType;

    let errorMessage: string | undefined;
    if (!content) errorMessage = "String mappingResult missing content";

    if (!fileType)
      errorMessage = errorMessage
        ? `${errorMessage}; missing fileType`
        : "String mappingResult missing fileType";

    return { content, fileType, fileName, isError, errorMessage };
  }

  return {
    content: null,
    fileType: null,
    fileName: null,
    isError: true,
    errorMessage: "Unknown mappingResult type",
  };
}

export function OutputNodeComponent({ data }: OutputNodeProps): JSX.Element {
  const axiosContext = useContext(AxiosContext);
  const { projectState, projectDispatch } = useContext(ProjectContext);

  const { currentTheme } = useDexterTheme();
  const { OUTPUT_CIRCLE_NODE_STYLE, SELECTED_NODE_BORDER_STYLE } =
    useTransformEngineStyles();

  const [_, setIsDraggingHandle] = useState(false);

  const { rfNodes } = projectState;
  const node = rfNodes.find((node) => node.id == data.dataID);
  const isSelected = node?.selected ?? false;

  const handle = data.handles?.[0] as TransformEngineNodeHandle;
  const handleRef = useRef<HTMLDivElement>(null);

  const outputNodeDescriptor: OutputNodeDescriptor =
    data.descriptor as OutputNodeDescriptor;

  // Unified readiness check via normalized mapping result.
  useEffect(() => {
    const outputNodeDescriptor = data.descriptor as OutputNodeDescriptor;
    const normalized = normalizeRawMappingResult(data.mappingResult);
    const hasValidResult = isFileInfoPair(normalized);

    if (outputNodeDescriptor?.isTransformReady && !hasValidResult) {
      const modifiedNodeData: TransformEngineNodeData = {
        ...data,
        mappingResult: null,
      };
      const modifiedOutputNodeDescriptor: OutputNodeDescriptor = {
        ...(data.descriptor as OutputNodeDescriptor),
        isTransformReady: false,
        type: FileType.UNDETECTED,
      };

      projectDispatch([
        "modifyOutputNodeData",
        { modifiedNodeData, modifiedOutputNodeDescriptor },
      ]);
    }
  }, [data, projectState.rfEdges, projectDispatch]);

  useEffect(() => {
    const outputNodeDescriptor = data.descriptor as OutputNodeDescriptor;

    if (
      outputNodeDescriptor &&
      outputNodeDescriptor.isTransformReady &&
      !nodeHasEdge(projectState.rfEdges, data.dataID)
    ) {
      const modifiedNodeData: TransformEngineNodeData = {
        ...data,
        mappingResult: null,
      };

      const modifiedOutputNodeDescriptor: OutputNodeDescriptor = {
        ...data.descriptor,
        isTransformReady: false,
        type: FileType.UNDETECTED,
      };

      projectDispatch([
        "modifyOutputNodeData",
        { modifiedNodeData, modifiedOutputNodeDescriptor },
      ]);
    }
  }, [projectState.rfEdges, data, projectDispatch]);

  const handleDownload = React.useCallback((): void => {
    const outputNode = projectState.rfNodes.find(
      (node) => data.dataID === node.data.dataID
    );
    const mappingResult = outputNode?.data.mappingResult;

    // First try the normalized structured result
    const normalized = normalizeRawMappingResult(mappingResult);

    let fileContent: string | null = null;
    let fileTypeEnum: FileType = FileType.UNDETECTED;
    let fileName: string = null;

    if (isFileInfoPair(normalized)) {
      fileContent = normalized.content;
      fileTypeEnum = normalized.fileType;
      fileName = normalized.fileName;
    } else if (typeof mappingResult === "string") {
      const { content, fileType, isError, errorMessage } =
        parseMappingResult(mappingResult);
      if (isError || !content) {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          `Transform file has no usable content to download${
            errorMessage ? `: ${errorMessage}` : ""
          }`
        );
        return;
      }
      fileContent = content;
      if (fileType) {
        fileTypeEnum = toFileTypeEnum(fileType);
      }
    }

    if (!fileContent) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Transform file has no usable content to download"
      );
      return;
    }

    let fileExtension = "txt";
    if (fileTypeEnum && fileTypeEnum in FileType) {
      fileExtension = fileTypeEnum.toLowerCase();
    }

    const safeFileTypePart = fileTypeEnum
      ? fileTypeEnum.toLowerCase()
      : "output";
    const downloadName = fileName
      ? `${fileName}_to_${safeFileTypePart}.${fileExtension}`
      : `transformed_output.${fileExtension}`;

    const blob = new Blob([fileContent], {
      type: getFileMimeType(fileExtension),
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    loggedNotify.positive(
      axiosContext,
      Component.TRANSFORM_ENGINE,
      "File downloaded successfully"
    );
  }, [projectState.rfNodes, data.dataID, axiosContext]);

  return (
    <div
      style={{
        position: "relative",
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        margin: "0 auto",
      }}
      tabIndex={0}
    >
      {/* Single Handle (centered vertically) */}
      {handle &&
        CreateTransformEngineNodeHandle(
          (handleRefElement) => {
            handleRef.current = handleRefElement;
          },
          handle,
          PathType.INPUT,
          CIRCLE_SIZE / 2 - HANDLE_SIZE / 2,
          setIsDraggingHandle
        )}

      {/* Circular Node */}
      <div
        style={{
          ...OUTPUT_CIRCLE_NODE_STYLE,
          ...(isSelected ? SELECTED_NODE_BORDER_STYLE : {}),
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            gap: 10,
            pointerEvents: "none",
          }}
        >
          <NodeLogo
            fileTypeFromDescriptor={
              (data.descriptor as OutputNodeDescriptor).type
            }
          />
          <div style={{ pointerEvents: "auto" }}>
            <StatefulTooltip
              content={"Download output"}
              placement={PLACEMENT.bottom}
              overrides={getTooltipOverrides(currentTheme)}
              showArrow
            >
              <Button
                size="compact"
                disabled={!outputNodeDescriptor.isTransformReady}
                onClick={handleDownload}
                style={{
                  opacity: outputNodeDescriptor.isTransformReady ? 1 : 0.5,
                  pointerEvents: outputNodeDescriptor.isTransformReady
                    ? "auto"
                    : "none",
                  padding: "0 10px",
                  minWidth: 32,
                  minHeight: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: currentTheme.colors.fileButtonColor,
                  backgroundColor: currentTheme.colors.secondary,
                }}
              >
                <IoMdDownload size={20} />
              </Button>
            </StatefulTooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
